import * as arrow from "apache-arrow";
import React, { useContext, useEffect, useRef, useState } from "react";

import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { isHistoricalDate } from "../../utils/mui_table_utils";
import { withSalesOrderCellDrilldown } from "../../utils/sales_order_cell_drilldown";

import { DatePivotParams } from "../../entities/date_pivot_params";
import { PivotViewConfig } from "../../entities/pivot_view_config";
import {
  getFlatTableData,
  getChunkedFlatTableData,
  getPivotTableData,
  getComparisonTableData,
} from "../../utils/mui_flat_table_data";
import { isChunkedParsedData } from "../../utils/parquet";
import {
  CHUNKED_MATRIX_TRANSFORM_IDS,
  useChunkedMatrixTableData,
} from "../../utils/chunked_inventory_health_matrix";
import type { FullParsedParquetData } from "../../utils/parquet";
import { MUIFlatTable } from "./MUIFlatTable";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import type {
  BoundCellExplanationConfig,
  RenderedViewBinding,
} from "../../entities/pivot_view_config";
import { createCommonViewContext } from "../../entities/common_view_context";

export type TableType = "pivot" | "flat";

export interface UniversalTableViewerProps<
  T extends DatePivotParams = DatePivotParams,
> {
  parsedData:
    | { arrowTable: arrow.Table; columns: arrow.Field<any>[] }
    | import("../../utils/parquet").ChunkedParsedParquetData
    | null;
  viewParams: T;
  viewControllerConfig?: PivotViewConfig<T>;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  tableType: TableType | undefined;
  loading: boolean;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
  renderedSource?: RenderedViewBinding;
}

function ChunkedMatrixTableWrapper<
  T extends DatePivotParams = DatePivotParams,
>(props: {
  parsedData: import("../../utils/parquet").ChunkedParsedParquetData;
  viewParams: T;
  viewControllerConfig: PivotViewConfig<T>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  columnHeaderMap?: Record<string, string>;
  shadeHistory?: boolean;
  showHeaderRow?: boolean;
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
  renderedSource?: RenderedViewBinding;
  sortDisplayedOnce: React.MutableRefObject<boolean>;
}) {
  const theme = useTheme();
  const {
    parsedData,
    viewParams,
    sortConfig,
    setSortConfig,
    expandedGroups = new Set(),
    setExpandedGroups = () => {},
    columnHeaderMap,
    shadeHistory,
    showHeaderRow,
    fetchSortKeys,
    renderedSource,
  } = props;
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const shouldExpandHeader = isHeaderExpanded || expandedGroups.has("*");
  const cellExplanation: BoundCellExplanationConfig | undefined =
    props.viewControllerConfig.cellExplanation && renderedSource
      ? {
          capture: (candidate) => {
            const draft = props.viewControllerConfig.cellExplanation?.capture({
              ...candidate,
              viewParams,
              source: renderedSource,
            });
            return draft
              ? Object.freeze({ ...draft, source: renderedSource })
              : undefined;
          },
          onExplain: props.viewControllerConfig.cellExplanation.onExplain,
          disabled: props.viewControllerConfig.cellExplanationEnabled === false,
        }
      : undefined;
  const onForecastCellExplain =
    props.viewControllerConfig.onForecastCellExplain && renderedSource
      ? (
          context: Parameters<
            NonNullable<PivotViewConfig<T>["onForecastCellExplain"]>
          >[0],
        ) => {
          const viewContext = createCommonViewContext({
            surfaceId: "forecast-tracking",
            measure: viewParams.measure,
            dateHierarchy: viewParams.dateHierarchy,
            minDate: viewParams.minDate,
            maxDate: viewParams.maxDate,
            segments: viewParams.segments,
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          });
          return props.viewControllerConfig.onForecastCellExplain?.(
            context,
            renderedSource,
            viewContext,
          );
        }
      : undefined;

  const tableData = useChunkedMatrixTableData({
    chunkedData: parsedData,
    viewParams,
    expandedGroups,
    setExpandedGroups,
    theme,
    sortConfig,
    setSortConfig,
    fetchSortKeys,
    onForecastCellExplain,
    cellExplanationDisabled:
      props.viewControllerConfig.cellExplanationEnabled === false,
    breakdownMeasures: props.viewControllerConfig?.breakdownMeasures,
    additionalMeasuresWithTotals:
      props.viewControllerConfig?.additionalMeasuresWithTotals,
    shadeHistory,
    plainDetailLabels: props.viewControllerConfig?.plainDetailLabels,
    enablePoCellPopover: props.viewControllerConfig?.enablePoCellPopover,
    enableTransferCellPopover:
      props.viewControllerConfig?.enableTransferCellPopover,
    enableUnfulfilledDemandPopover:
      props.viewControllerConfig?.enableUnfulfilledDemandPopover,
    enableForecastedSalesPopover:
      props.viewControllerConfig?.enableForecastedSalesPopover,
    showActualsForecastStrip:
      props.viewControllerConfig?.showActualsForecastStrip,
  });

  if (parsedData.rowCount === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          color: "text.secondary",
        }}
      >
        No data found for the current filters.
      </Box>
    );
  }

  if (!tableData.isReady) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        Loading table…
      </Box>
    );
  }

  if (!props.sortDisplayedOnce.current && !tableData.isSortReady) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        Loading table…
      </Box>
    );
  }

  if (tableData.isSortReady) {
    props.sortDisplayedOnce.current = true;
  }

  return (
    <MUIFlatTable
      columns={tableData.columns}
      getRow={tableData.getRow}
      rowCount={tableData.rowCount}
      columnHeaderMap={columnHeaderMap}
      sortConfig={sortConfig}
      columnTotals={tableData.columnTotals}
      headerBand={tableData.headerBand}
      expandedGroups={expandedGroups}
      onToggleGroup={
        setExpandedGroups
          ? (groupKey: string) => {
              setExpandedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(groupKey)) next.delete(groupKey);
                else next.add(groupKey);
                return next;
              });
            }
          : undefined
      }
      isHeaderExpanded={shouldExpandHeader}
      onToggleHeader={() => setIsHeaderExpanded(!isHeaderExpanded)}
      shadeHistory={shadeHistory}
      showHeaderRow={showHeaderRow}
      cellExplanation={cellExplanation}
    />
  );
}

export const UniversalTableViewer = <
  T extends DatePivotParams = DatePivotParams,
>(
  props: UniversalTableViewerProps<T>,
) => {
  const {
    parsedData,
    viewParams,
    viewControllerConfig,
    columnHeaderMap,
    columnFormatters,
    tableType,
    loading,
    sortConfig,
    setSortConfig,
    columnSchema,
    expandedGroups,
    setExpandedGroups,
    fetchSortKeys,
    renderedSource,
  } = props;

  const theme = useTheme();
  const firstMount = useRef(true);
  const sortDisplayedOnce = useRef(false);

  // Header expansion state for comparison tables
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // ❯ ACTUALS / FORECAST strip on the generic (non-chunked-matrix) pivot
  // path — split at the active run's as-of date, like the matrix path.
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const genericRunAsOf = (mainRun ?? lastSuccessfulRun)?.createdAt ?? null;
  const [genericActualsCollapsed, setGenericActualsCollapsed] = useState(false);

  // Check if wildcard expansion is requested
  const shouldExpandHeader = isHeaderExpanded || expandedGroups?.has("*");
  const cellExplanation: BoundCellExplanationConfig | undefined =
    viewControllerConfig?.cellExplanation && renderedSource
      ? {
          capture: (candidate) => {
            const draft = viewControllerConfig.cellExplanation?.capture({
              ...candidate,
              viewParams,
              source: renderedSource,
            });
            return draft
              ? Object.freeze({ ...draft, source: renderedSource })
              : undefined;
          },
          onExplain: viewControllerConfig.cellExplanation.onExplain,
          disabled: viewControllerConfig.cellExplanationEnabled === false,
        }
      : undefined;
  const onForecastCellExplain =
    viewControllerConfig?.onForecastCellExplain && renderedSource
      ? (
          context: Parameters<
            NonNullable<PivotViewConfig<T>["onForecastCellExplain"]>
          >[0],
        ) => {
          const viewContext = createCommonViewContext({
            surfaceId: "forecast-tracking",
            measure: viewParams.measure,
            dateHierarchy: viewParams.dateHierarchy,
            minDate: viewParams.minDate,
            maxDate: viewParams.maxDate,
            segments: viewParams.segments,
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          });
          return viewControllerConfig.onForecastCellExplain?.(
            context,
            renderedSource,
            viewContext,
          );
        }
      : undefined;

  useEffect(() => {
    if (parsedData && firstMount.current) {
      firstMount.current = false;
    }
  }, [parsedData]);

  // Sorting logic: clicking a column toggles its sort direction, single column sort by default, multi-column sort with shift key
  const handleSort = (
    key: string,
    e?: React.MouseEvent<HTMLTableCellElement>,
  ) => {
    setSortConfig((prev) => {
      const isShiftPressed = e?.shiftKey || false;
      const existingIndex = prev.findIndex((s) => s.key === key);
      const existingSort = existingIndex >= 0 ? prev[existingIndex] : null;

      // If shift not pressed, start with empty array (single column sort)
      const baseConfig = isShiftPressed ? [...prev] : [];

      if (existingSort) {
        // Column is already sorted - toggle direction or remove
        const newDirection = existingSort.direction === "asc" ? "desc" : null;

        if (newDirection) {
          const index = isShiftPressed ? existingIndex : 0;
          baseConfig[index] = {
            key,
            direction: newDirection,
            index,
          };
          return baseConfig;
        }

        return baseConfig.filter((_, idx) => idx !== existingIndex);
      }

      // New column - add as ascending
      return [
        ...baseConfig,
        { key, direction: "asc", index: baseConfig.length },
      ];
    });
  };

  if (loading || !parsedData) {
    return null;
  }

  // Chunked matrix path: matrix-shaped parquet (supply_walk_parallelized_matrix, etc.)
  const effectiveTransformId =
    (viewControllerConfig as { chunkedMatrixTransformId?: string })
      ?.chunkedMatrixTransformId ?? viewControllerConfig?.transformId;
  if (
    isChunkedParsedData(parsedData) &&
    viewControllerConfig &&
    effectiveTransformId &&
    CHUNKED_MATRIX_TRANSFORM_IDS.has(effectiveTransformId)
  ) {
    return (
      <ChunkedMatrixTableWrapper
        parsedData={parsedData}
        viewParams={viewParams}
        viewControllerConfig={viewControllerConfig}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
        columnHeaderMap={columnHeaderMap}
        shadeHistory={viewControllerConfig.shadeHistory}
        showHeaderRow={viewControllerConfig.showHeaderRow}
        fetchSortKeys={fetchSortKeys}
        renderedSource={renderedSource}
        sortDisplayedOnce={sortDisplayedOnce}
      />
    );
  }

  // Use the new virtualization-friendly data functions
  let tableData;

  // Chunked path: flat table only, no full arrowTable
  if (isChunkedParsedData(parsedData)) {
    tableData = getChunkedFlatTableData({
      rowCount: parsedData.rowCount,
      getRow: parsedData.getRow,
      columns: parsedData.columns,
      sortConfig,
      onSort: handleSort,
      columnSchema,
      setSortConfig,
    });
  } else if (viewControllerConfig?.getMatrix) {
    // Use custom getMatrix function if provided, otherwise fall back to default logic
    tableData = viewControllerConfig.getMatrix({
      arrowTable: parsedData.arrowTable,
      columns: parsedData.columns,
      viewParams,
      demandWalkConfig: viewControllerConfig.demandWalkConfig,
      purchaseOrderConfig: viewControllerConfig.purchaseOrderConfig,
      purchasingCalendarConfig: viewControllerConfig.purchasingCalendarConfig,
      sortConfig,
      onSort: handleSort,
      setSortConfig,
      columnSchema: columnSchema || {},
      theme,
      expandedGroups: expandedGroups || new Set(),
      setExpandedGroups: setExpandedGroups || (() => {}),
      columnHeaderMap,
      firstMount,
      onForecastCellExplain,
      cellExplanationDisabled:
        viewControllerConfig.cellExplanationEnabled === false,
    });
  } else {
    if (parsedData.columns.some((col) => col.name === "table_id")) {
      tableData = getComparisonTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        viewParams,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
        expandedGroups: expandedGroups || new Set(),
        setExpandedGroups: setExpandedGroups || (() => {}),
      });
    } else if (tableType === "flat") {
      tableData = getFlatTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
      });
    } else {
      tableData = getPivotTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        viewParams,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
      });
    }
  }

  if (!tableData) {
    return null;
  }

  const { getRow, rowCount, columnTotals } = tableData;

  // Demand sales-order popover: decorate the pivot builder's ACTUALS date
  // columns with the drilldown button (and the exact Nucleus display value)
  // without teaching the shared builder about Demand. Same altitude as the
  // strip below, which spreads each column and so keeps the renderers.
  const columns = viewControllerConfig?.enableSalesOrderPopover
    ? withSalesOrderCellDrilldown(tableData.columns, {
        viewParams,
        dateHierarchyColumnFilter:
          viewControllerConfig?.dateHierarchyColumnFilter,
        runAsOf: genericRunAsOf,
        theme,
        columnFormatters,
        shadeHistory: viewControllerConfig?.shadeHistory,
      })
    : tableData.columns;

  // Generic-path ❯ ACTUALS / FORECAST strip: date columns carry epoch-ms
  // keys, so the split/tint/boundary can be applied generically over any
  // builder's columns without touching the builders themselves.
  let bandedColumns = columns;
  let genericHeaderBand:
    | import("./MUIFlatTable").MUIFlatTableHeaderBand
    | undefined;
  if (
    viewControllerConfig?.showActualsForecastStrip &&
    genericRunAsOf != null
  ) {
    const isDateKey = (k: string) => k !== "TOTAL" && !isNaN(Number(k));
    const dateKeys = columns.map((c: any) => c.key).filter(isDateKey);
    const actualsKeys = dateKeys.filter((k: string) =>
      isHistoricalDate(Number(k), genericRunAsOf),
    );
    if (actualsKeys.length > 0) {
      const actualsSet = new Set(actualsKeys);
      const lastActualsKey = actualsKeys[actualsKeys.length - 1];
      const firstDateIdx = columns.findIndex((c: any) => isDateKey(c.key));
      const lead = columns.slice(0, firstDateIdx);
      const trailing = columns
        .slice(firstDateIdx)
        .filter((c: any) => !isDateKey(c.key));
      const forecastCols = columns.filter(
        (c: any) => isDateKey(c.key) && !actualsSet.has(c.key),
      );
      if (genericActualsCollapsed) {
        const summaryCol = {
          key: "__actuals_summary",
          label: "ACTUALS Σ",
          width: 120,
          sortable: false,
          boundaryRight: true,
          bandTint: "#F4F6F8",
          renderCell: (row: any) => {
            let sum = 0;
            let any = false;
            actualsKeys.forEach((k: string) => {
              const n = Number(String(row[k] ?? "").replace(/,/g, ""));
              if (row[k] != null && row[k] !== "" && !isNaN(n)) {
                sum += n;
                any = true;
              }
            });
            return (
              <Box sx={{ px: 1, textAlign: "right", width: "100%" }}>
                {any ? sum.toLocaleString() : ""}
              </Box>
            );
          },
        };
        bandedColumns = [...lead, summaryCol, ...forecastCols, ...trailing];
      } else {
        bandedColumns = columns.map((c: any) => ({
          ...c,
          ...(actualsSet.has(c.key) ? { bandTint: "#F4F6F8" } : {}),
          ...(c.key === lastActualsKey ? { boundaryRight: true } : {}),
        }));
      }
      genericHeaderBand = {
        leadColumnCount: lead.length,
        actualsColumnCount: genericActualsCollapsed ? 1 : actualsKeys.length,
        forecastColumnCount: forecastCols.length,
        trailingColumnCount: trailing.length,
        collapsed: genericActualsCollapsed,
        onToggleCollapsed: () => setGenericActualsCollapsed((v) => !v),
        stickyLeftWidth: lead.reduce(
          (acc: number, c: any) => acc + (c.sticky ? Number(c.width) || 0 : 0),
          0,
        ),
      };
    }
  }

  return (
    <MUIFlatTable
      columns={bandedColumns}
      getRow={getRow}
      rowCount={rowCount}
      columnHeaderMap={columnHeaderMap}
      columnFormatters={columnFormatters}
      sortConfig={sortConfig}
      shadeHistory={viewControllerConfig?.shadeHistory}
      showHeaderRow={viewControllerConfig?.showHeaderRow}
      headerBand={genericHeaderBand}
      columnTotals={columnTotals}
      expandedGroups={expandedGroups}
      onToggleGroup={
        setExpandedGroups
          ? (groupKey: string) => {
              setExpandedGroups((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(groupKey)) {
                  newSet.delete(groupKey);
                } else {
                  newSet.add(groupKey);
                }
                return newSet;
              });
            }
          : undefined
      }
      isHeaderExpanded={shouldExpandHeader}
      onToggleHeader={() => setIsHeaderExpanded(!isHeaderExpanded)}
      cellExplanation={cellExplanation}
    />
  );
};

export { MUIFlatTable };
